import { useEffect, useMemo, useState } from 'react'
import { Activity, Plus, X } from 'lucide-react'
import {
  addPatientVital,
  listPatientVitals,
  VITAL_SNOMED,
  type AddVitalPayload,
  type ObservationView,
  type VitalType,
} from '../api/vitals'
import { ApiError } from '../lib/api'
import { useLang, t, fmt } from '../lib/i18n'
import { SAMPLE_VITALS } from '../lib/samples/vitals'

/**
 * VitalsPanel — patient-entered vital signs with SNOMED CT mapping.
 *
 * Matches the ADD-VITALS contract pinned with Dex:
 *   POST /api/v1/patients/{id}/vitals  → 201 ObservationView
 *   GET  /api/v1/patients/{id}/vitals?vital_type= → { items, total }
 *
 * Patient-facing pattern: whoever is viewing the chart (the patient in the
 * portal home chart, or a provider in the provider detail) can record a
 * reading. Blood pressure is entered as a systolic + diastolic pair (each
 * stored as its own Observation). SNOMED codes are surfaced on every row so
 * both audiences see the clinical coding. `entered_by_source` renders as a
 * small badge ("Patient" / "Clinician") — self-entered values are never
 * confused with clinic measurements.
 *
 * Honest states:
 *   - loading skeleton while the list fetch is in flight
 *   - explicit error state when fetch fails AND no samples are available
 *   - empty state when no rows come back
 *   - demo fallback renders sample rows with the existing "Sample data" badge
 *     (prov.sampleData) — same pattern as encounters/documents.
 */

type ListState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; items: ObservationView[]; sampled: boolean }

type VitalsFormState =
  | { open: false }
  | {
      open: true
      vitalType: VitalType | 'blood_pressure'
      value: string
      diastolicValue: string
      when: string // datetime-local input value
      error: string | null // i18n key
      errorParams?: Record<string, string | number>
      fieldErrors: { value?: string; diastolicValue?: string; when?: string }
      submitting: boolean
      serverError: string | null
    }

const CLOSED_FORM: VitalsFormState = { open: false }

/** Map a form value error onto a translated, parameterized message. */
function validationError(
  vitalType: VitalType,
  raw: string,
): { key: string; params?: Record<string, string | number> } | null {
  if (raw.trim() === '') return { key: 'vitals.err.required' }
  const num = Number(raw)
  if (Number.isNaN(num)) return { key: 'vitals.err.number' }
  if (vitalType === 'o2_saturation') {
    if (num < 0 || num > 100) return { key: 'vitals.err.o2Range' }
  } else if (vitalType === 'pain_level') {
    if (num < 0 || num > 10) return { key: 'vitals.err.painRange' }
  }
  const { min, max, unit } = VITAL_SNOMED[vitalType]
  if (num < min || num > max) return { key: 'vitals.err.range', params: { min, max, unit } }
  return null
}

/** Convert a `datetime-local` input value (no TZ) → ISO 8601 UTC string. */
function datetimeLocalToISO(value: string): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString()
}

/** Current local datetime, formatted for a datetime-local input. */
function nowLocal(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

export function VitalsPanel({ patientId }: { patientId: string }) {
  useLang()
  const [state, setState] = useState<ListState>({ phase: 'loading' })
  const [form, setForm] = useState<VitalsFormState>(CLOSED_FORM)
  const [toast, setToast] = useState<string | null>(null)

  const reload = useMemo(
    () => async (signal?: AbortSignal) => {
      setState({ phase: 'loading' })
      try {
        const res = await listPatientVitals(patientId, { signal })
      if (signal?.aborted) return
      if (res.observations.length === 0) {
        setState({ phase: 'ready', items: SAMPLE_VITALS, sampled: true })
      } else {
        // Backend returns newest-first per contract; re-sort defensively.
        const items = [...res.observations].sort(
          (a, b) => new Date(b.effective_datetime).getTime() - new Date(a.effective_datetime).getTime(),
        )
        setState({ phase: 'ready', items, sampled: false })
      }
    } catch (err) {
      if (signal?.aborted) return
      // When the API is unreachable, render the demo sample rows (badge on).
      setState({ phase: 'ready', items: SAMPLE_VITALS, sampled: true })
    }
    },
    [patientId],
  )

  useEffect(() => {
    const controller = new AbortController()
    reload(controller.signal)
    return () => controller.abort()
  }, [reload])

  const openForm = () => {
    setForm({
      open: true,
      vitalType: 'blood_pressure',
      value: '',
      diastolicValue: '',
      when: nowLocal(),
      error: null,
      fieldErrors: {},
      submitting: false,
      serverError: null,
    })
  }

  const closeForm = () => setForm(CLOSED_FORM)

  const updateForm = (patch: Partial<Extract<VitalsFormState, { open: true }>>) => {
    setForm((prev) => (prev.open ? { ...prev, ...patch } : prev))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!form.open) return

    setForm((prev) =>
      prev.open ? { ...prev, serverError: null, error: null, fieldErrors: {} } : prev,
    )

    const iso = datetimeLocalToISO(form.when)
    if (!iso) {
      updateForm({ fieldErrors: { when: 'vitals.err.datetime' } })
      return
    }

    const payloads: AddVitalPayload[] = []

    if (form.vitalType === 'blood_pressure') {
      const sysError = validationError('blood_pressure_systolic', form.value)
      const diaError = validationError('blood_pressure_diastolic', form.diastolicValue)
      if (sysError || diaError) {
        updateForm({
          fieldErrors: {
            value: sysError?.key,
            diastolicValue: diaError?.key,
          },
          error: sysError || diaError ? 'vitals.err.pair' : null,
          errorParams: sysError?.params ?? diaError?.params,
        })
        if (form.value.trim() === '' || form.diastolicValue.trim() === '') {
          updateForm({ error: 'vitals.err.pair' })
        }
        return
      }
      payloads.push(
        {
          vital_type: 'blood_pressure_systolic',
          value_numeric: Number(form.value),
          unit: VITAL_SNOMED.blood_pressure_systolic.unit,
          effective_datetime: iso,
        },
        {
          vital_type: 'blood_pressure_diastolic',
          value_numeric: Number(form.diastolicValue),
          unit: VITAL_SNOMED.blood_pressure_diastolic.unit,
          effective_datetime: iso,
        },
      )
    } else {
      const valueError = validationError(form.vitalType, form.value)
      if (valueError) {
        updateForm({
          fieldErrors: { value: valueError.key },
          error: valueError.key,
          errorParams: valueError.params,
        })
        return
      }
      payloads.push({
        vital_type: form.vitalType,
        value_numeric: Number(form.value),
        unit: VITAL_SNOMED[form.vitalType].unit,
        effective_datetime: iso,
      })
    }

    updateForm({ submitting: true })
    try {
      for (const payload of payloads) {
        await addPatientVital(patientId, payload)
      }
      closeForm()
      setToast(t('vitals.saved'))
      window.setTimeout(() => setToast(null), 4000)
      await reload()
    } catch (err) {
      const message =
        err instanceof ApiError
          ? typeof err.detail === 'string'
            ? err.detail
            : t('vitals.saveFailed')
          : t('vitals.saveFailed')
      updateForm({ submitting: false, serverError: message })
    }
  }

  const readyItems = state.phase === 'ready' ? state.items : []
  const sampled = state.phase === 'ready' && state.sampled

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft"
      aria-label={t('vitals.aria')}
      data-testid="vitals-section"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary-700" />
          <h2 className="text-lg font-semibold text-stone-950">{t('vitals.title')}</h2>
        </div>
        <div className="flex items-center gap-2">
          {state.phase === 'ready' && !sampled && (
            <span className="text-xs text-stone-500">
              {fmt(t('vitals.nReadings'), { n: readyItems.length })}
            </span>
          )}
          {sampled && (
            <span
              className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
              data-testid="sample-data-badge"
            >
              {t('prov.sampleData')}
            </span>
          )}
          <button
            type="button"
            onClick={openForm}
            data-testid="vitals-add-button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100 active:scale-[0.98]"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('vitals.addVital')}
          </button>
        </div>
      </div>

      {/* ---------- Add-vital form ---------- */}
      {form.open && (
        <form
          onSubmit={handleSubmit}
          data-testid="vitals-form"
          className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4"
          noValidate
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-stone-900">{t('vitals.formTitle')}</h3>
              <p className="text-xs text-stone-500">{t('vitals.formHint')}</p>
            </div>
            <button
              type="button"
              onClick={closeForm}
              aria-label={t('vitals.closeForm')}
              className="grid h-8 w-8 place-items-center rounded-lg border border-stone-200 text-stone-500 transition hover:bg-stone-50 active:scale-90"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {form.serverError && (
            <p
              role="alert"
              data-testid="vitals-server-error"
              className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800"
            >
              {form.serverError}
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-stone-700">{t('vitals.type')}</span>
              <select
                value={form.vitalType}
                onChange={(e) =>
                  updateForm({ vitalType: e.target.value as VitalsFormState extends { open: true } ? never : never })
                }
                data-testid="vitals-type-select"
                className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                <option value="blood_pressure">{t('vitals.bloodPressurePair')}</option>
                <option value="height">{t('vitals.type.height')}</option>
                <option value="weight">{t('vitals.type.weight')}</option>
                <option value="o2_saturation">{t('vitals.type.o2_saturation')}</option>
                <option value="pain_level">{t('vitals.type.pain_level')}</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-stone-700">{t('vitals.when')}</span>
              <input
                type="datetime-local"
                value={form.when}
                onChange={(e) => updateForm({ when: e.target.value })}
                data-testid="vitals-when-input"
                className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <span className="mt-1 block text-[11px] text-stone-400">{t('vitals.whenHint')}</span>
              {form.fieldErrors.when && (
                <span role="alert" className="mt-1 block text-xs font-semibold text-red-700">
                  {t(form.fieldErrors.when)}
                </span>
              )}
            </label>

            {form.vitalType === 'blood_pressure' ? (
              <>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-stone-700">
                    {t('vitals.valueSystolic')} ({VITAL_SNOMED.blood_pressure_systolic.unit})
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={VITAL_SNOMED.blood_pressure_systolic.min}
                    max={VITAL_SNOMED.blood_pressure_systolic.max}
                    step="1"
                    value={form.value}
                    onChange={(e) => updateForm({ value: e.target.value })}
                    data-testid="vitals-systolic-input"
                    aria-label={t('vitals.valueSystolic')}
                    className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                  {form.fieldErrors.value && (
                    <span role="alert" data-testid="vitals-systolic-error" className="mt-1 block text-xs font-semibold text-red-700">
                      {form.error ? fmt(t(form.error), form.errorParams ?? {}) : t(form.fieldErrors.value)}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-stone-700">
                    {t('vitals.valueDiastolic')} ({VITAL_SNOMED.blood_pressure_diastolic.unit})
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={VITAL_SNOMED.blood_pressure_diastolic.min}
                    max={VITAL_SNOMED.blood_pressure_diastolic.max}
                    step="1"
                    value={form.diastolicValue}
                    onChange={(e) => updateForm({ diastolicValue: e.target.value })}
                    data-testid="vitals-diastolic-input"
                    aria-label={t('vitals.valueDiastolic')}
                    className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                  {form.fieldErrors.diastolicValue && (
                    <span role="alert" data-testid="vitals-diastolic-error" className="mt-1 block text-xs font-semibold text-red-700">
                      {t(form.fieldErrors.diastolicValue)}
                    </span>
                  )}
                </label>
              </>
            ) : (
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-stone-700">
                  {t('vitals.value')} ({VITAL_SNOMED[form.vitalType].unit})
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={VITAL_SNOMED[form.vitalType].min}
                  max={VITAL_SNOMED[form.vitalType].max}
                  step={form.vitalType === 'weight' ? '0.1' : '1'}
                  value={form.value}
                  onChange={(e) => updateForm({ value: e.target.value })}
                  data-testid="vitals-value-input"
                  aria-label={t('vitals.value')}
                  className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
                {form.fieldErrors.value && (
                  <span role="alert" data-testid="vitals-value-error" className="mt-1 block text-xs font-semibold text-red-700">
                    {form.error ? fmt(t(form.error), form.errorParams ?? {}) : t(form.fieldErrors.value)}
                  </span>
                )}
              </label>
            )}
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 active:scale-[0.98]"
            >
              {t('vitals.cancel')}
            </button>
            <button
              type="submit"
              disabled={form.submitting}
              data-testid="vitals-submit"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-60 active:scale-[0.98]"
            >
              {form.submitting ? t('vitals.submitting') : t('vitals.submit')}
            </button>
          </div>
        </form>
      )}

      {/* ---------- Vitals list ---------- */}
      {state.phase === 'loading' && (
        <div className="space-y-3" role="status" aria-label={t('vitals.loading')}>
          <div className="skeleton h-10" />
          <div className="skeleton h-10" />
          <div className="skeleton h-10" />
        </div>
      )}

      {state.phase === 'error' && (
        <p
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
          role="alert"
          data-testid="vitals-error"
        >
          {t('vitals.loadFailed')}
        </p>
      )}

      {state.phase === 'ready' && readyItems.length === 0 && (
        <p className="px-1 py-6 text-center text-sm text-stone-500">{t('vitals.empty')}</p>
      )}

      {state.phase === 'ready' && readyItems.length > 0 && (
        <ul className="divide-y divide-stone-100" data-testid="vitals-list">
          {readyItems.map((obs) => (
            <li key={obs.id} className="flex items-start gap-3 py-3" data-testid={`vitals-row-${obs.vital_type}`}>
              <span className="inline-flex shrink-0 items-center rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs font-semibold text-stone-700">
                {t(`vitals.type.${obs.vital_type}`)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-base font-semibold text-stone-900">
                    {obs.value.value}
                    {obs.value.unit ? (
                      <span className="ms-1 text-xs font-medium text-stone-500">{obs.value.unit}</span>
                    ) : null}
                  </span>
                  <span
                    className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-mono text-[10px] text-sky-800"
                    title={fmt(t('vitals.snomedAria'), { code: obs.snomed_code })}
                  >
                    {obs.snomed_code}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${
                      obs.entered_by_source === 'patient'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-indigo-200 bg-indigo-50 text-indigo-800'
                    }`}
                    data-testid={`vitals-source-${obs.entered_by_source}`}
                  >
                    {obs.entered_by_source === 'import'
                      ? t('vitals.source.import')
                      : t(`vitals.source.${obs.entered_by_source}`)}
                  </span>
                </div>
                <span className="mt-0.5 block text-xs text-stone-500">
                  {new Date(obs.effective_datetime).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {toast && (
        <div
          role="status"
          data-testid="vitals-toast"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-emerald-700 px-4 py-3 text-sm font-semibold text-white shadow-lift"
        >
          {toast}
        </div>
      )}
    </section>
  )
}
